"use client";

import { ofetch } from "ofetch";
import { newsletterPreferencesPaths } from "routes";
import useSWRMutation from "swr/mutation";
import * as z from "zod";

import type { SubscribeToNewsletterApiResponseData } from "@/api/newsletter/route";
import Button from "@/components/atoms/Button";
import Checkbox from "@/components/forms/controls/Checkbox";
import TextInput from "@/components/forms/controls/TextInput";
import Form from "@/components/forms/Form";
import { env } from "@/env";
import { useAsyncRouter } from "@/hooks/useAsyncRouter";
import { useZodForm } from "@/hooks/useZodForm";
import { useTranslation } from "@/i18n/client";
import { AllowedLanguages } from "@/types/generic";

interface NewsletterFormProps {
  locale: AllowedLanguages;
  location?: string | null;
  searchQuery?: string | null;
}

export const NewsletterForm = ({
  locale,
  location,
  searchQuery,
}: NewsletterFormProps) => {
  const { t } = useTranslation(locale);
  const router = useAsyncRouter();
  const schema = z.object({
    email: z.email(t("common.error.invalid_email")).trim().toLowerCase(),
    first_name: z
      .string()
      .min(1, t("common.error.required"))
      .transform((val) => {
        return val.replace(/(<([^>]+)>)/gi, "");
      }),
    last_name: z
      .string()
      .min(1, t("common.error.required"))
      .transform((val) => {
        return val.replace(/(<([^>]+)>)/gi, "");
      }),
    // this checkbox is used as protection from spambots. Idea is that spambot will click it as true and in this case we don't send form at all
    submit_confirmation_box: z.literal(false),
  });

  const form = useZodForm({
    schema,
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
      // this checkbox is used as protection from spambots. Idea is that spambot will click it as true and in this case we don't send form at all
      submit_confirmation_box: false,
    },
  });

  async function subscribe(
    url: string,
    {
      arg,
    }: {
      arg: z.infer<typeof schema>;
    },
  ) {
    return await ofetch<SubscribeToNewsletterApiResponseData>(url, {
      method: "POST",
      body: {
        ...arg,
        locale,
      },
    });
  }

  const { trigger, error } = useSWRMutation("/api/newsletter", subscribe);

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    const response = await trigger(data);
    if (!response?.key) return;

    const url = new URL(
      `${env.NEXT_PUBLIC_BASE_URL}/${locale}/myvivaldis/${newsletterPreferencesPaths[locale]}`,
    );
    url.searchParams.set("key", response.key);
    url.searchParams.set("location", location ?? "");
    url.searchParams.set("searchQuery", searchQuery ?? "");
    await router.push(url.toString());
  };

  return (
    <Form
      form={form}
      className="grid grid-cols-12 gap-x-6 gap-y-6 xl:gap-x-10"
      onSubmit={handleSubmit}
      noValidate={true}
    >
      <div className="col-span-12 md:col-span-6">
        <TextInput
          label={t("common.form.first_name.label")}
          name="first_name"
          labelClasses="text-white"
          autoComplete="given-name"
        />
      </div>
      <div className="col-span-12 md:col-span-6">
        <TextInput
          label={t("common.form.last_name.label")}
          name="last_name"
          labelClasses="text-white"
          autoComplete="family-name"
        />
      </div>
      <div className="col-span-12 md:col-span-6 2xl:col-span-8">
        <TextInput
          label={t("common.form.email.label")}
          name="email"
          type="email"
          labelClasses="text-white"
          autoComplete="email"
        />
      </div>
      <div className="col-span-12 flex md:col-span-6 2xl:col-span-4">
        <Button
          type="submit"
          className="btn-white btn-lg btn-outline mt-auto whitespace-nowrap border-2"
          hasLoader={true}
          isLoading={form.formState.isSubmitting}
          disabled={form.formState.isSubmitting}
        >
          {t("components.newsletter_form.submit")}
        </Button>
      </div>
      {error ? (
        <div className="col-span-12">
          <div className="form-error text-lg">
            {t("common.error.unknown_error")}
          </div>
        </div>
      ) : null}
      <div className="col-span-12">
        <div className="inline-flex flex-wrap items-center gap-1 font-normal text-white">
          {t("components.newsletter_form.privacy_statement.label")}
        </div>
      </div>

      {/* this checkbox is used as protection from spambots. Idea is that spambot will click it as true and in this case we don't send form at all */}
      <Checkbox
        label="I agree to submit this form."
        name="submit_confirmation_box"
        asBoolean={true}
        className="hidden"
      />
    </Form>
  );
};

export default NewsletterForm;
